/** Shared condition / finish / language options for inventory intake and copy editing. */

import { lookupGameForProductLine, type InventoryLookupGame } from './inventoryNav';

export type FinishValue = 'normal' | 'foil' | 'etched' | 'signed' | 'reverse_holofoil';

export const CONDITIONS = [
  { value: 'NM', label: 'Near Mint (NM)' },
  { value: 'LP', label: 'Lightly Played (LP)' },
  { value: 'MP', label: 'Moderately Played (MP)' },
  { value: 'HP', label: 'Heavily Played (HP)' },
  { value: 'Damaged', label: 'Damaged' },
] as const;

export const FINISHES: Array<{ value: FinishValue; label: string }> = [
  { value: 'normal', label: 'Non-Foil' },
  { value: 'foil', label: 'Foil' },
  { value: 'etched', label: 'Etched' },
  { value: 'signed', label: 'Signed' },
  { value: 'reverse_holofoil', label: 'Reverse Holofoil' },
];

/** Short codes stored on copies / batch items (JA not JP — matches parity + variant maps). */
export const LANGUAGES = [
  { value: 'EN', label: 'English' },
  { value: 'JA', label: 'Japanese' },
  { value: 'FR', label: 'French' },
  { value: 'DE', label: 'German' },
  { value: 'ES', label: 'Spanish' },
  { value: 'IT', label: 'Italian' },
  { value: 'PT', label: 'Portuguese' },
  { value: 'KO', label: 'Korean' },
  { value: 'RU', label: 'Russian' },
  { value: 'ZHS', label: 'Chinese Simplified' },
  { value: 'ZHT', label: 'Chinese Traditional' },
] as const;

export function isFoilFromFinish(finish: FinishValue): boolean {
  return finish === 'foil' || finish === 'etched' || finish === 'reverse_holofoil';
}

export function finishFromCopy(isFoil: boolean, printing: string | null | undefined): FinishValue {
  const normalized = (printing || 'normal').toLowerCase();
  if (normalized === 'reverse_holofoil') return 'reverse_holofoil';
  if (normalized === 'signed') return 'signed';
  if (normalized === 'etched') return 'etched';
  if (normalized === 'foil' || isFoil) return 'foil';
  return 'normal';
}

export function finishFromItem(item: {
  foil?: boolean | null;
  etched?: boolean | null;
  printing?: string | null;
}): FinishValue {
  if (item.printing) {
    return finishFromCopy(Boolean(item.foil), item.printing);
  }
  if (item.etched) return 'etched';
  return finishFromCopy(Boolean(item.foil), null);
}

export function finishLabel(isFoil: boolean, printing?: string | null): string {
  const value = finishFromCopy(isFoil, printing);
  return FINISHES.find((item) => item.value === value)?.label ?? 'Non-Foil';
}

export function displayCondition(condition: string): string {
  if (condition === 'DMG' || condition === 'DAMAGED') return 'Damaged';
  return condition;
}

export function formConditionMatchesCopy(formCondition: string, copyCondition: string): boolean {
  return formCondition === displayCondition(copyCondition);
}

/** Staff-facing stamps. Etched stays a Magic printing; Pokémon/Lorcana treat etched as foil. */
const LOOKUP_FINISHES: Record<InventoryLookupGame, readonly FinishValue[]> = {
  mtg: ['normal', 'foil', 'etched', 'signed'],
  pokemon: ['normal', 'foil', 'reverse_holofoil'],
  lorcana: ['normal', 'foil'],
};

export function finishesForLookupGame(game: InventoryLookupGame): Array<{ value: FinishValue; label: string }> {
  const allowed = new Set<FinishValue>(LOOKUP_FINISHES[game]);
  return FINISHES.filter((item) => allowed.has(item.value));
}

/** Known lines use game stamps. Unknown lines keep every shared finish. */
export function finishesForProductLine(
  productLine?: string | null,
): Array<{ value: FinishValue; label: string }> {
  const game = lookupGameForProductLine(productLine);
  if (!game) return FINISHES;
  return finishesForLookupGame(game);
}

export function withCurrentFinish(
  options: Array<{ value: FinishValue; label: string }>,
  current: FinishValue,
): Array<{ value: FinishValue; label: string }> {
  if (options.some((item) => item.value === current)) return options;
  const known = FINISHES.find((item) => item.value === current);
  return [...options, known ?? { value: current, label: current }];
}

const FILTER_STAMP_LABELS: Partial<Record<FinishValue, string>> = {
  foil: 'Foil / Holofoil',
};

/** Lookup printing filter. Foil Only / Non-Foil Only stay, because etched counts as foil. */
export function finishFilterOptions(game: InventoryLookupGame): Array<{ value: string; label: string }> {
  return [
    { value: 'all', label: 'All printings' },
    { value: 'true', label: 'Foil Only' },
    { value: 'false', label: 'Non-Foil Only' },
    ...finishesForLookupGame(game).map((item) => ({
      value: item.value,
      label: FILTER_STAMP_LABELS[item.value] ?? item.label,
    })),
  ];
}

/** Badge text. Reverse holofoil stays distinct; etched renders as foil. */
export function foilTreatmentLabel(item: {
  foil?: boolean | null;
  etched?: boolean | null;
  printing?: string | null;
}): 'REV HOLO' | 'FOIL' | null {
  const finish = finishFromItem(item);
  if (finish === 'reverse_holofoil') return 'REV HOLO';
  if (isFoilFromFinish(finish)) return 'FOIL';
  return null;
}
